// Страница тарифов: /pricing.
//
// Смотрят её двое: тот, кто ещё ничего не сделал и решает, стоит ли начинать,
// и тот, кто уже упёрся в лимит. Первому нужна короткая карточка с ценой,
// второму — конкретная строка «сколько именно мне дадут».
//
// Отсюда три этажа, а не два: карточки тарифов, десять строк, по которым
// выбирают, и всё остальное — свёрнутым. Тридцать строк подряд не помогают
// решить, а мешают: по ним нельзя пробежать глазами, их пролистывают до конца
// страницы. Но и выбросить их нельзя — по ним проверяют, входит ли конкретное.
//
// Содержимое приезжает с сервера (документ из админки), но страница обязана
// рисоваться и без него: пока запрос идёт — и если он не удался — показывается
// встроенный документ. Витрина, которая ждёт сеть, чтобы показать цену, хуже
// витрины с чуть устаревшей ценой.
import { Fragment, useEffect, useState, type ReactElement } from 'react';
import { LanguagePicker, useI18n, useT } from '../i18n';
import { AppMenu, AppMenuBlock, AppMenuRow, Logo } from '../ui/shell';
import { PersonalizationPanel } from '../theme';
import { useAuth } from '../auth';
import { navigate } from '../router';
import { useDocumentMeta } from '../seo';
import { pageContent } from '../content';
import { fetchPublicPricing } from './api';
import {
  defaultPricingDoc,
  resolvePricing,
  type PricingDoc,
  type PricingView,
  type ViewFeature,
} from './content';
import { PLANS, type Limit, type PlanId } from './plans';
import styles from './PricingPage.module.css';

type Billing = 'monthly' | 'yearly';

/** Идентификатор скрытой части таблицы: на него ссылается `aria-controls`. */
const REST_ID = 'pricing-rest';

function money(amount: number, currency: string): string {
  // Дробная цена показывается с двумя знаками, целая — без: «6.00 €» на
  // витрине выглядит как результат вычисления, а не как цена.
  const value = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${value} ${currency}`;
}

/**
 * Документ тарифов: сначала встроенный, потом — сохранённый в админке.
 *
 * Ошибку запроса не показываем и не логируем в интерфейс: для читателя витрины
 * «контент чуть старее» и «контент из базы» неразличимы, а сообщение об ошибке
 * на странице с ценами пугает сильнее, чем помогает.
 */
function usePricingDoc(): PricingDoc {
  const [doc, setDoc] = useState<PricingDoc>(defaultPricingDoc);

  useEffect(() => {
    let alive = true;
    void fetchPublicPricing().then((loaded) => {
      if (alive && loaded) setDoc(loaded);
    });
    return () => {
      alive = false;
    };
  }, []);

  return doc;
}

/** Меню страницы: тулбара планировщика здесь нет, уходить куда-то надо. */
function PricingMenu(): ReactElement {
  const t = useT();
  const { lang } = useI18n();
  const { user, signOut, requestSignIn } = useAuth();
  // Подписи контентных страниц — из их же текста: заводить для меню второй
  // набор названий значило бы разойтись с тем, что написано на самой странице.
  const c = pageContent(lang);

  return (
    <AppMenu
      sections={[
        {
          id: 'trips',
          title: t('menu.section.trips'),
          children: (
            <AppMenuBlock>
              <a className="btn" href="/" data-menu-close>
                {t('pricing.back')}
              </a>
              {/* Ссылка на лайфхаки стоит и здесь: это вторая публичная
                  страница, и из выдачи попадают то на одну, то на другую. */}
              <a className="btn" href="/lifehacks" data-menu-close>
                {t('lh.title')}
              </a>
              {user && (
                <a className="btn" href="/trips" data-menu-close>
                  {t('trips.title')}
                </a>
              )}
              <a className="btn" href="/how-it-works" data-menu-close>
                {c.how.title}
              </a>
              <a className="btn" href="/templates" data-menu-close>
                {c.templates.title}
              </a>
              <a className="btn" href="/about" data-menu-close>
                {c.about.title}
              </a>
            </AppMenuBlock>
          ),
        },
        {
          id: 'view',
          title: t('menu.section.view'),
          children: (
            <>
              <AppMenuRow label={t('app.language')}>
                <LanguagePicker />
              </AppMenuRow>
              <AppMenuBlock>
                <PersonalizationPanel />
              </AppMenuBlock>
            </>
          ),
        },
        {
          id: 'account',
          title: t('menu.section.account'),
          children: user ? (
            <>
              <AppMenuRow label={t('account.title')}>
                <span className={styles.menuEmail}>{user.name.trim() || user.email}</span>
              </AppMenuRow>
              <AppMenuBlock>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-menu-close
                  onClick={() => void signOut()}
                >
                  {t('auth.signout')}
                </button>
              </AppMenuBlock>
            </>
          ) : (
            <AppMenuBlock>
              <button
                type="button"
                className="btn btn-primary"
                data-menu-close
                onClick={requestSignIn}
              >
                {t('auth.signin')}
              </button>
            </AppMenuBlock>
          ),
        },
      ]}
    />
  );
}

/**
 * Значение лимита словами.
 *
 * «100 / мес» и «100» — разные обещания, и по одной цифре их не различить:
 * первое возобновляется, второе занимаешь один раз навсегда. Поэтому единица
 * измерения приезжает из самого лимита, а не дописывается в тексте строки.
 *
 * Словарь значений не редактируется из админки: это лексика таблицы, а не
 * контент — переписать «без лимита» значит сломать смысл колонки, а не улучшить.
 */
export function LimitValue({ limit }: { limit: Limit }): ReactElement {
  const t = useT();
  switch (limit.kind) {
    case 'unlimited':
      return <span className={styles.valueStrong}>{t('pricing.value.unlimited')}</span>;
    case 'yes':
      return <span className={styles.valueStrong}>{t('pricing.value.yes')}</span>;
    case 'none':
      return <span className={styles.valueNone}>{t('pricing.value.none')}</span>;
    case 'text':
      return <span>{t(limit.key)}</span>;
    case 'count':
      return (
        <span>
          {t(limit.per === 'month' ? 'pricing.value.perMonth' : 'pricing.value.total', {
            count: limit.value,
          })}
        </span>
      );
  }
}

/**
 * Строка сравнения. Одна и та же и в главном, и в раскрытой части: разойдись
 * они вёрсткой — раскрытие читалось бы как переход в другую таблицу.
 */
function FeatureRow({ feature }: { feature: ViewFeature }): ReactElement {
  return (
    <tr className={styles.row}>
      <th scope="row" className={styles.tdFeature}>
        {feature.title}
      </th>
      <td className={styles.tdPlan}>
        <LimitValue limit={feature.free} />
      </td>
      <td className={`${styles.tdPlan} ${styles.tdPro}`}>
        <LimitValue limit={feature.pro} />
      </td>
    </tr>
  );
}

function PlanCard({
  plan,
  view,
  billing,
  onChoose,
}: {
  plan: PlanId;
  view: PricingView;
  billing: Billing;
  onChoose(): void;
}): ReactElement {
  const t = useT();
  const isPro = plan === 'pro';
  const { currency, monthly, yearly } = view.price;
  // Годовой план всё равно показан «в месяц»: сравнивать 48 и 6 человек не
  // станет, он сравнит 4 и 6. Полная сумма — строкой ниже, без неё это обман.
  const perMonth = billing === 'yearly' ? yearly / 12 : monthly;

  return (
    <section className={`${styles.card} ${isPro ? styles.cardPro : ''}`}>
      {/* Скидка за год названа один раз — на переключателе. В карточке она
          стояла бы в сантиметре от него же и читалась как вторая скидка. */}
      <h2 className={styles.cardName}>
        {view.text(isPro ? 'pricing.plan.pro' : 'pricing.plan.free')}
      </h2>

      <p className={styles.price}>
        {isPro
          ? t('pricing.price.perMonth', { amount: money(Math.round(perMonth), currency) })
          : t('pricing.price.free')}
      </p>
      <p className={styles.priceHint}>
        {isPro
          ? billing === 'yearly'
            ? t('pricing.price.yearlyHint', { amount: money(yearly, currency) })
            : t('pricing.price.monthlyHint')
          : ' '}
      </p>

      <p className={styles.tagline}>
        {view.text(isPro ? 'pricing.plan.pro.tagline' : 'pricing.plan.free.tagline')}
      </p>

      <ul className={styles.highlights}>
        {view.highlights[plan].map((feature) => (
          // Название слева, значение справа — как в большой таблице. Обратный
          // порядок склеивал бы число с существительным в одну фразу
          // («1 Поездок»), а согласовывать её по всем пяти языкам ради
          // нескольких строк в карточке незачем.
          <li key={feature.id} className={styles.highlight}>
            <span className={styles.highlightName}>{feature.title}</span>
            <span className={styles.highlightValue}>
              <LimitValue limit={feature[plan]} />
            </span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className={`btn ${isPro ? 'btn-primary' : ''} ${styles.cta}`}
        onClick={onChoose}
      >
        {view.text(isPro ? 'pricing.cta.pro' : 'pricing.cta.free')}
      </button>
    </section>
  );
}

export function PricingPage(): ReactElement {
  const t = useT();
  const { lang } = useI18n();
  const { user, requestSignIn } = useAuth();
  const [billing, setBilling] = useState<Billing>('yearly');
  // Раскрытая часть закрыта при входе на любом экране: она отвечает на вопрос
  // «а вот это входит?», который возникает после решения, а не до него.
  const [showAll, setShowAll] = useState(false);

  const doc = usePricingDoc();
  const view = resolvePricing(doc, lang);

  // Заголовок вкладки — отдельный текст, не тот, что стоит над ценами: в
  // истории браузера и в закладках «Тарифы» ни о чём не говорит.
  useDocumentMeta({
    title: view.text('pricing.seo.title'),
    description: view.text('pricing.seo.description'),
  });

  /**
   * Кнопки обоих тарифов пока ведут в одно место — ко входу: без аккаунта
   * лимиты некуда записывать, а приёма оплаты ещё нет. Когда появится касса,
   * разойдётся только ветка Pro.
   */
  function choose(): void {
    if (user) navigate('/trips');
    else requestSignIn();
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <PricingMenu />
        <Logo title={t('app.brand')} />
        <h1 className={styles.title}>{view.text('pricing.title')}</h1>
      </header>

      <p className={styles.lede}>{view.text('pricing.subtitle')}</p>

      {/* Переключатель периода стоит над карточками, а не внутри Pro: цену он
          меняет у одного тарифа, но выбирает человек между двумя. */}
      <div className={styles.billing} role="group" aria-label={view.text('pricing.title')}>
        {(['monthly', 'yearly'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            className={`${styles.billingBtn} ${billing === mode ? styles.billingOn : ''}`}
            aria-pressed={billing === mode}
            onClick={() => setBilling(mode)}
          >
            {t(mode === 'monthly' ? 'pricing.billing.monthly' : 'pricing.billing.yearly')}
            {mode === 'yearly' && view.yearlyDiscount > 0 && (
              <span className={styles.billingSave}>
                {t('pricing.billing.save', { percent: view.yearlyDiscount })}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className={styles.cards}>
        {PLANS.map((plan) => (
          <PlanCard key={plan} plan={plan} view={view} billing={billing} onChoose={choose} />
        ))}
      </div>

      <p className={styles.ctaNote}>{view.text('pricing.cta.note')}</p>

      {/* ── Сравнение ───────────────────────────────────────────────────── */}
      {(view.keyFeatures.length > 0 || view.restCount > 0) && (
        <section className={styles.block}>
          <h2 className={styles.h2}>{view.text('pricing.compare')}</h2>

          {/* Одна таблица на все разделы, а не таблица на раздел: колонки должны
              стоять по одной вертикали через весь экран, иначе глаз каждый раз
              заново ищет, где тут Pro. По той же причине спрятанные строки
              лежат внутри этой таблицы, а не во второй под ней. */}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col" className={styles.thFeature}>
                    {t('pricing.col.feature')}
                  </th>
                  <th scope="col" className={styles.thPlan}>
                    {view.text('pricing.plan.free')}
                  </th>
                  <th scope="col" className={`${styles.thPlan} ${styles.thPro}`}>
                    {view.text('pricing.plan.pro')}
                  </th>
                </tr>
              </thead>

              {/* Главное — без заголовка раздела: он тут один и назывался бы
                  тем же словом, что и заголовок всего блока. */}
              <tbody>
                {view.keyFeatures.map((feature) => (
                  <FeatureRow key={feature.id} feature={feature} />
                ))}
              </tbody>

              {/* Раскрытие живёт в самой таблице отдельной строкой: кнопка под
                  таблицей раскрывала бы то, что от неё оторвано, а на телефоне
                  ещё и уезжала бы за экран вместе с концом таблицы. */}
              {view.restCount > 0 && (
                <tbody>
                  <tr>
                    <td colSpan={3} className={styles.moreCell}>
                      <button
                        type="button"
                        className={styles.more}
                        aria-expanded={showAll}
                        aria-controls={REST_ID}
                        onClick={() => setShowAll((on) => !on)}
                      >
                        {/* Тот же значок, что у групп библиотеки: одна фигура
                            на оба состояния — свёрнутое отличается поворотом,
                            а не другим символом. */}
                        <svg
                          className={`${styles.chevron} ${showAll ? styles.chevronOpen : ''}`}
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.4}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                        {showAll
                          ? t('pricing.compare.less')
                          : t('pricing.compare.more', { count: view.restCount })}
                      </button>
                    </td>
                  </tr>
                </tbody>
              )}

              {/* Скрытое не размонтируется, а прячется атрибутом: `hidden` на
                  `tbody` понимают и скринридер, и поиск по странице, а
                  свёрнутые строки при этом остаются в разметке — витрину читают
                  не только глазами. */}
              <tbody id={REST_ID} hidden={!showAll}>
                {view.sections.map((section) => (
                  <Fragment key={section.id}>
                    <tr>
                      <th scope="colgroup" colSpan={3} className={styles.sectionCell}>
                        <span className={styles.sectionName}>{section.title}</span>
                        {section.hint && <span className={styles.sectionHint}>{section.hint}</span>}
                      </th>
                    </tr>
                    {section.features.map((feature) => (
                      <FeatureRow key={feature.id} feature={feature} />
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Разовые покупки ─────────────────────────────────────────────── */}
      {view.extras.length > 0 && (
        <section className={styles.block}>
          <h2 className={styles.h2}>{view.text('pricing.extras.title')}</h2>
          <p className={styles.blockHint}>{view.text('pricing.extras.hint')}</p>
          <ul className={styles.extras}>
            {view.extras.map((extra) => (
              <li key={extra.id} className={styles.extra}>
                <span className={styles.extraName}>{extra.title}</span>
                <span className={styles.extraPrice}>
                  {t('pricing.extras.price', { amount: money(extra.price, view.price.currency) })}
                </span>
                <span className={styles.extraHint}>{extra.hint}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── В работе ────────────────────────────────────────────────────── */}
      {view.soon.length > 0 && (
        <section className={styles.block}>
          <h2 className={styles.h2}>{view.text('pricing.soon.title')}</h2>
          <p className={styles.blockHint}>{view.text('pricing.soon.hint')}</p>
          <ul className={styles.soon}>
            {view.soon.map((entry) => (
              <li key={entry.id} className={styles.soonItem}>
                {entry.title}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Вопросы ─────────────────────────────────────────────────────── */}
      {view.faq.length > 0 && (
        <section className={styles.block}>
          <h2 className={styles.h2}>{view.text('pricing.faq.title')}</h2>
          <dl className={styles.faq}>
            {view.faq.map((entry) => (
              <div key={entry.id} className={styles.faqItem}>
                <dt className={styles.faqQ}>{entry.question}</dt>
                <dd className={styles.faqA}>{entry.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <footer className={styles.footer}>
        <a className={styles.back} href="/">
          {t('pricing.back')}
        </a>
      </footer>
    </div>
  );
}
